type AppConfig = { BACKEND_BASE: string; CDN_BASE: string };

function readEnv(): Partial<AppConfig> {
  const vite = (typeof import.meta !== 'undefined' && (import.meta as any)?.env) || {};
  const next = (typeof process !== 'undefined' && (process as any).env) || {};
  const win  = (typeof window !== 'undefined' && (window as any).__APP_CONFIG__) || {};
  return {
    BACKEND_BASE: next.NEXT_PUBLIC_BACKEND_BASE ?? vite.VITE_BACKEND_BASE ?? next.PUBLIC_BACKEND_BASE ?? win.BACKEND_BASE,
    CDN_BASE:     next.NEXT_PUBLIC_CDN_BASE     ?? vite.VITE_CDN_BASE     ?? next.CDN_BASE           ?? win.CDN_BASE,
  };
}

export const CONFIG: AppConfig = {
  BACKEND_BASE: (readEnv().BACKEND_BASE || 'https://ftcanvaphotobookeditor.pages.dev').replace(/\/$/, ''),
  CDN_BASE: (readEnv().CDN_BASE || 'https://photobook-cdn.b-cdn.net').replace(/\/$/, ''),
};

// Expose for runtime debug inside Canva
if (typeof window !== 'undefined') {
  (window as any).__APP_CONFIG__ = { ...CONFIG, // allow console override below
    __override(v: Partial<AppConfig>) { Object.assign((window as any).__APP_CONFIG__, v); }
  };
}

export const apiUrl = (path: string) => `${CONFIG.BACKEND_BASE}/${String(path).replace(/^\//, '')}`;
export const cdnUrl = (path: string) => `${CONFIG.CDN_BASE}/${String(path).replace(/^\//, '')}`;

// Runtime guard – fail early in Canva if misconfigured
if (typeof window !== 'undefined') {
  if (!CONFIG.BACKEND_BASE || !CONFIG.CDN_BASE) {
    console.error('[config] Missing BACKEND_BASE/CDN_BASE', CONFIG);
  }
}

// Legacy compatibility exports
const isCanvaEnvironment = typeof window !== 'undefined' &&
  window.location.host.includes('canva-apps.com');

const isDevelopment = typeof window !== 'undefined' &&
  (window.location.host.includes('localhost') || window.location.host.includes('127.0.0.1'));

interface LegacyAppConfig {
  BACKEND_BASE: string;
  CDN_BASE: string;
  isCanvaEnvironment: boolean;
  isDevelopment: boolean;
}

export const config: LegacyAppConfig = {
  BACKEND_BASE: CONFIG.BACKEND_BASE,
  CDN_BASE: CONFIG.CDN_BASE,
  isCanvaEnvironment,
  isDevelopment
};

export function validateConfig(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (isCanvaEnvironment) {
    if (!CONFIG.BACKEND_BASE) errors.push('BACKEND_BASE is required when running in Canva');
    if (!CONFIG.CDN_BASE) errors.push('CDN_BASE is required when running in Canva');
  }
  return { isValid: errors.length === 0, errors };
}

export function showConfigBanner(): void {
  const validation = validateConfig();
  if (!validation.isValid && isCanvaEnvironment) {
    console.warn('[config] Configuration issues detected:', validation.errors);
    const banner = document.createElement('div');
    banner.style.cssText = `position: fixed; top: 0; left: 0; right: 0; background: #ff6b35; color: white; padding: 8px 16px; font-size: 12px; z-index: 10000; text-align: center;`;
    banner.textContent = 'Backend not configured - some features may not work';
    setTimeout(() => banner.parentNode?.removeChild(banner), 5000);
    document.body.appendChild(banner);
  }
}

export function getUserKey(): string {
  const urlParams = new URLSearchParams(window.location.search);
  const urlUserKey = urlParams.get('userKey') || urlParams.get('designId');
  if (urlUserKey) {
    localStorage.setItem('photobook:userKey', urlUserKey);
    return urlUserKey;
  }
  let userKey = localStorage.getItem('photobook:userKey');
  if (!userKey) {
    userKey = `user_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    localStorage.setItem('photobook:userKey', userKey);
  }
  return userKey;
}

// Log configuration on load
console.log('[config] App configuration:', JSON.stringify({
  PUBLIC_BACKEND_BASE: CONFIG.BACKEND_BASE,
  CDN_BASE: CONFIG.CDN_BASE,
  isCanvaEnvironment,
  isDevelopment
}));
